
import { Component, Vue, Prop, Watch } from "vue-property-decorator"
import { namespace, Getter } from "vuex-class"

import vSelect from "vue-select"
import Tags from "@/components/Fields/Tags.vue"
import AreaSelect from "@/components/Fields/AreaSelect.vue"
import FormGroup from "../Fields/FormGroup.vue"

import { Template, Area, Status, Severity, CaseDetails, TemplateDetails } from "@/types"

import TextAreaToggleExpand from "@/components/Fields/TextAreaToggleExpand.vue"
import { CurrentUser } from "@/types/user"
import { TenantFeatures } from "@/types/tenantFeatures"

import api from "@/services/api"
const caseStore = namespace("cases")

@Component({
	components: {
		"v-select": vSelect,
		"text-area-expand": TextAreaToggleExpand,
		"tags": Tags,
		"area-select": AreaSelect,
		"form-group": FormGroup
	}
})
export default class CaseDetailsHeader extends Vue {
	// ----- Vuex ----- //

	@Getter("getMe")
	public me!: CurrentUser;

	@Getter("isFeatureEnabled")
	private isFeatureEnabled!: (featureKey: TenantFeatures) => boolean

	@caseStore.Action("setFieldLockPolling")
	private setFieldLockPolling!: ({ caseDetails, fieldId }: { caseDetails: CaseDetails, fieldId: string })=> void;

	@caseStore.Action("cancelFieldLockPolling")
	private cancelFieldLockPolling!: ({ caseDetails, fieldId }: { caseDetails: CaseDetails, fieldId: string }) => void;

	@caseStore.Action public linkCase!: (arg0: any) => void;
	@caseStore.Action private saveCaseProperty!: ({ caseColumnKey, newValue }: { caseColumnKey: string, newValue: string | null }) => Promise<void>;

	@caseStore.Getter("getStatuses") public statuses!: Status[];
	@caseStore.Getter("getSeverities") public severities!: Severity[];

	@caseStore.Getter("getCreatableCategories") public categories!: Template[] | null;
	@caseStore.State("areas") public areas!: Area[];

	// ----- Props ----- //

	@Prop()
	public currentCase!: CaseDetails

	@Prop()
	public template!: TemplateDetails

	@Prop({ default: false })
	public canEditCurrentCase!: boolean

	@Prop({ default: true })
	public titleTemplateEnabled!: boolean

	@Prop({ default: "Case" })
	public caseName!: string

	@Prop({ default: "Record" })
	public recordName!: string

	@Prop({ default: false })
	public showAreas!: boolean

	@Prop({ default: false })
	public showStatus!: boolean

	@Prop({ default: false })
	public showTags!: boolean

	@Prop({ default: false })
	public showSeverity!: boolean

	@Prop({ default: false })
	public preventTemplateChange!: boolean

	// ----- Data Properties ----- //

	/**
	 * Dictionary of fields with changes currently being saved
	 * */
	private savingFields: { [key: string]: boolean } = {
		"summary": false
	}

	fieldErrors: { [key: string]: {
		error: string,
		clearTimeout: NodeJS.Timeout
	} } = {}

	private pendingSaveTitle: string = this.currentCase.title

	// ----- Computed Properties ----- //

	private get allowLockedFieldsToBeEditedEnabled (): boolean {
		return this.isFeatureEnabled(TenantFeatures.AllowLockedFieldsToBeEdited)
	}

	private get lockedForCurrentUser (): { [key: string]: boolean } {
		if (!this.currentCase || !this.currentCase.builtInFieldLocks) return {}

		const locks = this.currentCase.builtInFieldLocks

		return Object.keys(locks).reduce((acc: { [key: string]: boolean }, fieldId) => {
			const lock = locks[fieldId]
			if (!lock) {
				acc[fieldId] = false
			} else if (this.allowLockedFieldsToBeEditedEnabled) {
				acc[fieldId] = false
			} else {
				// Field lock is owned by another user, so it should be locked for current user.
				acc[fieldId] = lock.id !== this.me.id
			}
			return acc
		}, {})
	}

	// ----- Watchers ----- //

	@Watch("currentCase.title")
	private onTitleChanged (newValue: string): void {
		this.pendingSaveTitle = newValue
	}

	// ----- Methods ----- //

	/**
	 * Emits changes made to currentCase to parent component and waits for parent to resolve
	 * */
	private async onCaseChanged (): Promise<void> {
		this.$emit("caseChanged")
	}

	/**
	 * Updates title for currentCase
	*/
	private async updateTitle (): Promise<void> {
		this.currentCase.title = this.pendingSaveTitle
		await this.fieldLostFocus("title", this.currentCase.title)
	}

	private async onCaseCategoryChanged (templateId: string): Promise<void> {
		this.$emit("templateIdChanged", templateId)
	}

	private fieldSetError (fieldId: string, error: string) {
		if (this.fieldErrors[fieldId]) {
			clearTimeout(this.fieldErrors[fieldId].clearTimeout)
		}

		Vue.set(this.fieldErrors, fieldId, {
			error,
			clearTimeout: setTimeout(() => {
				Vue.delete(this.fieldErrors, fieldId)
			}, 5000)
		})
	}

	private async fieldGotFocus (fieldId: string, title?: string): Promise<void> {
		if (!this.currentCase?.id || !this.canEditCurrentCase) return

		try {
			const result = await api.selectCaseField(this.currentCase.id, fieldId, true)
			this.setFieldLockPolling({ caseDetails: this.currentCase, fieldId })

			if (this.allowLockedFieldsToBeEditedEnabled && !result.success) {
				// Another user is editing, show warning
				const inUseByName = result.inUseByName || "another user"
				this.fieldSetError(fieldId, `Field is also in use by ${inUseByName}, changes may be overwritten`)
			} else if (!this.allowLockedFieldsToBeEditedEnabled && !result.success) {
				// Feature disabled and field locked by another user, unable to edit.
				const inUseByName = result.inUseByName || "another user"
				this.fieldSetError(fieldId, `Unable to edit ${title || "field"} - In use by ${inUseByName}`)
			}
		} catch (err) {
			this.fieldSetError(fieldId, `Unable to edit ${title || "field"} - ${err}`)
			console.error(err)
		}
	}

	private async fieldLostFocus (fieldId: string, newValue: string): Promise<void> {
		if (this.currentCase!.id && this.canEditCurrentCase) {
			try {
				this.savingFields[fieldId] = true
				await this.saveCaseProperty({ caseColumnKey: fieldId, newValue })
			} finally {
				this.savingFields[fieldId] = false
			}
			this.cancelFieldLockPolling({ caseDetails: this.currentCase, fieldId: fieldId })
		}
	}
}
